import { Task, Team } from '../types';
import { subDays, isWithinInterval, startOfMonth, endOfMonth } from 'date-fns';

export const calculateMetrics = (tasks: Task[], teams: Team[]) => {
  const today = new Date();
  const lastMonth = subDays(today, 30);
  
  const recentTasks = tasks.filter(task => 
    new Date(task.createdAt) >= lastMonth
  );

  const completedTasks = recentTasks.filter(task => 
    task.status === 'Completed'
  );

  const taskCompletionRate = recentTasks.length > 0
    ? Math.round((completedTasks.length / recentTasks.length) * 100)
    : 0;

  const teamProductivity = teams.length > 0
    ? Math.round((completedTasks.length / teams.length) * 100)
    : 0;

  const activeMembers = teams.reduce((acc, team) => 
    acc + team.members.length, 0
  );

  const averageResponseTime = recentTasks.reduce((acc, task) => {
    const created = new Date(task.createdAt);
    const completed = task.status === 'Completed' 
      ? new Date(task.updatedAt)
      : new Date();
    return acc + (completed.getTime() - created.getTime());
  }, 0) / (recentTasks.length || 1);

  return {
    taskCompletionRate,
    teamProductivity,
    activeMembers,
    averageResponseTime: Math.round(averageResponseTime / (1000 * 60 * 60))
  };
};

export const calculateTrends = (tasks: Task[]) => {
  const today = new Date();
  const currentMonth = {
    start: startOfMonth(today),
    end: endOfMonth(today)
  };
  
  const lastMonth = {
    start: startOfMonth(subDays(today, 30)),
    end: endOfMonth(subDays(today, 30))
  };

  const currentMonthTasks = tasks.filter(task =>
    isWithinInterval(new Date(task.createdAt), currentMonth)
  );
  
  const lastMonthTasks = tasks.filter(task =>
    isWithinInterval(new Date(task.createdAt), lastMonth)
  );

  const currentCompletionRate = calculateCompletionRate(currentMonthTasks);
  const lastCompletionRate = calculateCompletionRate(lastMonthTasks);
  const completionTrend = calculateTrendPercentage(currentCompletionRate, lastCompletionRate);

  const currentEfficiency = calculateEfficiency(currentMonthTasks);
  const lastEfficiency = calculateEfficiency(lastMonthTasks);
  const efficiencyTrend = calculateTrendPercentage(currentEfficiency, lastEfficiency);

  return {
    completionRate: currentCompletionRate,
    completionTrend,
    efficiency: currentEfficiency,
    efficiencyTrend
  };
};

const calculateCompletionRate = (tasks: Task[]) => {
  if (tasks.length === 0) return 0;
  const completed = tasks.filter(task => task.status === 'Completed').length;
  return Math.round((completed / tasks.length) * 100);
};

const calculateEfficiency = (tasks: Task[]) => {
  if (tasks.length === 0) return 0;
  const completedOnTime = tasks.filter(task => {
    if (task.status !== 'Completed') return false;
    const dueDate = new Date(task.dueDate);
    const completedDate = new Date(task.updatedAt);
    return completedDate <= dueDate;
  }).length;
  return Math.round((completedOnTime / tasks.length) * 100);
};

const calculateTrendPercentage = (current: number, previous: number) => {
  if (previous === 0) return current > 0 ? 100 : 0;
  return Math.round(((current - previous) / previous) * 100);
};